import type { Address, ContactDetail, ContactListItem, JobDetail } from "@nmt/shared";
import { CONTACT_CATEGORY_LABELS, type JobListItem, type PropertyPreview, formatPhone } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { errorMessage, get, patch, post } from "../api";
import { useUsers, useWorkflows } from "../lookups";
import { useSession } from "../session";
import { type Placed, StreetWithSuggestions } from "./address-suggest";
import { Alert, Button, Checkbox, Dialog, Field, SelectField, TextAreaField, addressLine, cx, formatCents, parseDollars } from "./ui";

export function AddressFields({ value, onChange, legend, onPlaced, action }: { value: Address; onChange: (a: Address) => void; legend?: string; onPlaced?: (p: Placed) => void; action?: React.ReactNode }) {
  const set = (k: keyof Address) => (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, [k]: e.target.value });
  return (
    <fieldset className="grid gap-3 sm:grid-cols-6">
      {(legend || action) && (
        <legend className="mb-1 flex w-full flex-wrap items-center justify-between gap-2 text-sm font-semibold text-ink-soft sm:col-span-6">
          <span>{legend}</span>
          {action}
        </legend>
      )}
      <StreetWithSuggestions
        className="sm:col-span-6"
        value={value.street}
        onType={(street) => onChange({ ...value, street })}
        onPick={(p) => {
          onChange({ ...value, street: p.street, city: p.city, state: p.state, zip: p.zip });
          onPlaced?.(p);
        }}
      />
      <Field className="sm:col-span-6" label="Street line 2" value={value.street2} onChange={set("street2")} autoComplete="address-line2" />
      <Field className="sm:col-span-3" label="City" value={value.city} onChange={set("city")} autoComplete="address-level2" />
      <Field className="sm:col-span-1" label="State" value={value.state} onChange={set("state")} autoComplete="address-level1" />
      <Field className="sm:col-span-2" label="ZIP" value={value.zip} onChange={set("zip")} autoComplete="postal-code" inputMode="numeric" />
    </fieldset>
  );
}

export const emptyAddress: Address = { street: "", street2: "", city: "", state: "OK", zip: "" };

type ContactForm = {
  typeId: string;
  kind: "residential" | "commercial";
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  phone: string;
  phone2: string;
  paymentTerms: string;
  notes: string;
  inactive?: boolean;
} & Address;

const blankContact: ContactForm = { typeId: "", kind: "residential", firstName: "", lastName: "", company: "", email: "", phone: "", phone2: "", paymentTerms: "", notes: "", ...emptyAddress };

/** Add or edit a contact of any type. When adding, it warns about likely duplicates before saving. */
export function ContactDialog({
  contact,
  defaultTypeId,
  onClose,
  onSaved,
}: {
  contact?: ContactDetail;
  /** For a new contact; otherwise the first customer type. */
  defaultTypeId?: string;
  onClose: () => void;
  onSaved?: (c: ContactDetail) => void;
}) {
  const { me } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const types = me?.contactTypes ?? [];
  const firstCustomerType = types.find((t) => t.category === "customer")?.id ?? types[0]?.id ?? "";
  const [form, setForm] = useState<ContactForm>(contact ? pick(contact) : { ...blankContact, typeId: defaultTypeId ?? firstCustomerType });
  const set = (k: keyof ContactForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const category = types.find((t) => t.id === form.typeId)?.category ?? "other";
  const isCustomer = category === "customer";
  const business = form.kind === "commercial";

  // Likely duplicates, checked as they type (adding only).
  const [probe, setProbe] = useState({ phone: "", email: "", name: "" });
  useEffect(() => {
    if (contact) return;
    const t = window.setTimeout(() => setProbe({ phone: form.phone, email: form.email, name: business ? form.company : `${form.firstName} ${form.lastName}`.trim() }), 400);
    return () => window.clearTimeout(t);
  }, [contact, form.phone, form.email, form.firstName, form.lastName, form.company, business]);
  const dups = useQuery({
    queryKey: ["contact-dups", probe],
    queryFn: () => get<ContactListItem[]>(`/api/contacts/duplicates?${new URLSearchParams(probe)}`),
    enabled: !contact && !!(probe.phone || probe.email || probe.name),
  });

  const save = useMutation({
    mutationFn: () => (contact ? patch<ContactDetail>(`/api/contacts/${contact.id}`, form) : post<ContactDetail>("/api/contacts", form)),
    onSuccess: async (c) => {
      qc.setQueryData(["contact", c.id], c);
      await qc.invalidateQueries({ queryKey: ["contacts"] });
      onClose();
      if (onSaved) onSaved(c);
      else if (!contact) navigate(`/contacts/${c.id}`);
    },
  });

  return (
    <Dialog
      wide
      title={contact ? `Edit ${contact.displayName}` : "New contact"}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="contact-form" busy={save.isPending}>
            {contact ? "Save" : "Add contact"}
          </Button>
        </>
      }
    >
      <form
        id="contact-form"
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <div className="grid gap-3 sm:grid-cols-2 sm:items-end">
          <SelectField label="Type" value={form.typeId} onChange={set("typeId")} required hint={CONTACT_CATEGORY_LABELS[category].quickbooks}>
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </SelectField>
          <div role="radiogroup" aria-label="Person or business" className="inline-flex w-fit rounded-[var(--radius-control)] bg-mist p-0.5 sm:mb-6">
            {(["residential", "commercial"] as const).map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={form.kind === k}
                onClick={() => setForm((f) => ({ ...f, kind: k }))}
                className={cx("rounded-[6px] px-3 py-1.5 text-sm font-semibold", form.kind === k ? "bg-white shadow-sm" : "text-muted hover:text-ink")}
              >
                {k === "residential" ? (isCustomer ? "Residential" : "Person") : isCustomer ? "Commercial" : "Business"}
              </button>
            ))}
          </div>
        </div>
        {business && <Field label="Company name" required value={form.company} onChange={set("company")} autoFocus={!contact} />}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={business ? "Contact first name" : "First name"} value={form.firstName} onChange={set("firstName")} autoFocus={!contact && !business} />
          <Field label={business ? "Contact last name" : "Last name"} value={form.lastName} onChange={set("lastName")} />
          {!business && <Field className="sm:col-span-2" label="Company (optional)" value={form.company} onChange={set("company")} hint="E.g. the insurance carrier an adjuster works for" />}
          <Field label="Mobile phone" type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" />
          <Field label="Other phone" type="tel" value={form.phone2} onChange={set("phone2")} />
          <Field className="sm:col-span-2" label="Email" type="email" value={form.email} onChange={set("email")} autoComplete="email" />
          {(category === "vendor" || category === "subcontractor") && (
            <Field label="Payment terms" value={form.paymentTerms} onChange={set("paymentTerms")} placeholder="Net 30" hint="Their bills are due by these terms unless the bill says otherwise." />
          )}
          {contact && (category === "vendor" || category === "subcontractor") && (
            <div className="sm:col-span-2">
              <Checkbox
                label="Inactive"
                description="We don't use them anymore: off the insurance list and reminders, and not offered for new work orders. Their history stays."
                checked={!!form.inactive}
                onChange={(v) => setForm((f) => ({ ...f, inactive: v }))}
              />
            </div>
          )}
        </div>
        <AddressFields value={form} onChange={(a) => setForm((f) => ({ ...f, ...a }))} legend="Mailing address" />
        <TextAreaField label="Notes" value={form.notes} onChange={set("notes")} rows={2} />
        {!!dups.data?.length && (
          <Alert tone="info">
            <div className="font-semibold">Already in your contacts?</div>
            <ul className="mt-1">
              {dups.data.map((d) => (
                <li key={d.id}>
                  <Link to={`/contacts/${d.id}`} className="font-medium text-brand underline" onClick={onClose}>
                    {d.displayName}
                  </Link>{" "}
                  · {[d.typeName, formatPhone(d.phone), d.email, d.city].filter(Boolean).join(" · ")}
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </form>
    </Dialog>
  );
}

function pick(c: ContactDetail): ContactForm {
  return {
    typeId: c.typeId,
    kind: c.kind,
    firstName: c.firstName,
    lastName: c.lastName,
    company: c.company,
    email: c.email,
    phone: c.phone,
    phone2: c.phone2,
    paymentTerms: c.paymentTerms,
    notes: c.notes,
    inactive: c.inactive,
    street: c.street,
    street2: c.street2,
    city: c.city,
    state: c.state,
    zip: c.zip,
  };
}

type NewCustomer = { typeId: string; kind: "residential" | "commercial"; firstName: string; lastName: string; company: string; phone: string; email: string } & Address;

/**
 * New job: type, title, sales rep, lead source, and where (the customer's address by default). From a
 * customer's page it's for them; from the Jobs page (no `customer`) pick one, or add a new customer
 * and they're saved together.
 */
export function NewJobDialog({ customer: given, onClose }: { customer?: Pick<ContactDetail, "id" | "displayName"> & Address; onClose: () => void }) {
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  const pickedDetail = useQuery({ queryKey: ["contact", picked?.id], queryFn: () => get<ContactDetail>(`/api/contacts/${picked!.id}`), enabled: !!picked });
  const [isNew, setIsNew] = useState(false);
  const customerTypes = (useSession().me?.contactTypes ?? []).filter((t) => t.category === "customer");
  const [nc, setNc] = useState<NewCustomer>({ typeId: "", kind: "residential", firstName: "", lastName: "", company: "", phone: "", email: "", ...emptyAddress });
  useEffect(() => {
    if (!nc.typeId && customerTypes[0]) setNc((x) => ({ ...x, typeId: customerTypes[0]!.id }));
  }, [customerTypes, nc.typeId]);
  // Already a contact? Look them up by phone or name as they're typed.
  const lookFor = nc.phone.replace(/\D/g, "").length >= 7 ? nc.phone : `${nc.firstName} ${nc.lastName}`.trim() || nc.company;
  const maybe = useQuery({ queryKey: ["contact-search", lookFor], queryFn: () => get<{ items: ContactListItem[] }>(`/api/contacts?q=${encodeURIComponent(lookFor)}&limit=5`), enabled: isNew && lookFor.trim().length >= 3 });
  const customer = given ?? (picked && pickedDetail.data ? pickedDetail.data : null);
  const { me } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const workflows = useWorkflows();
  const users = useUsers();
  const [workflowId, setWorkflowId] = useState("");
  const [title, setTitle] = useState("");
  const [salesRepId, setRep] = useState(me?.user.id ?? "");
  const [leadSource, setLeadSource] = useState("");
  const [elsewhere, setElsewhere] = useState(false);
  const [address, setAddress] = useState<Address>({ ...emptyAddress, city: given?.city ?? "", state: given?.state ?? "OK" });
  // Where the job's address is (a picked suggestion or "use my location"), and what the county has there.
  const [position, setPosition] = useState<{ lat: number; lon: number } | null>(null);
  const [property, setProperty] = useState<PropertyPreview | null>(null);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);
  const jobStreet = isNew && !elsewhere ? nc.street : elsewhere ? address.street : (customer?.street ?? "");
  const sameAddress = useQuery({
    queryKey: ["jobs-at", jobStreet],
    queryFn: () => get<{ items: JobListItem[] }>(`/api/jobs?status=all&limit=5&q=${encodeURIComponent(jobStreet)}`),
    enabled: jobStreet.trim().length >= 6 && /\d/.test(jobStreet),
  });
  /** The county's owner fills an empty name (a suggestion; anything typed stays). */
  const learn = async (at: { lat: number; lon: number }) => {
    setPosition(at);
    const p = await get<PropertyPreview>(`/api/places/property?lat=${at.lat}&lon=${at.lon}`).catch(() => null);
    setProperty(p);
    const n = p?.name;
    if (n && (isNew || (!given && !picked))) {
      setNc((x) => (x.firstName || x.lastName || x.company ? x : { ...x, kind: n.kind, firstName: n.firstName, lastName: n.lastName, company: n.company }));
    }
  };
  const useMyLocation = () => {
    setLocateError(null);
    if (!navigator.geolocation) return setLocateError("This browser can't share its location.");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const at = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        try {
          const here = await get<{ address: { street: string; city: string; state: string; zip: string } | null }>(`/api/places/reverse?lat=${at.lat}&lon=${at.lon}`);
          const a = here.address;
          if (!a) setLocateError("Couldn't find a street address here. Type it in.");
          else {
            const filled = { street: a.street, street2: "", city: a.city, state: a.state || "OK", zip: a.zip };
            if (given || (picked && !isNew)) (setElsewhere(true), setAddress(filled));
            else (setIsNew(true), setNc((x) => ({ ...x, ...filled })));
          }
          await learn(at);
        } catch (err) {
          setLocateError(errorMessage(err));
        } finally {
          setLocating(false);
        }
      },
      (err) => (setLocating(false), setLocateError(err.code === err.PERMISSION_DENIED ? "Location is blocked for this site. Allow it in your browser's settings, or type the address." : "Couldn't get your location. Try again or type the address.")),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 },
    );
  };

  useEffect(() => {
    if (!workflowId && workflows.data?.[0]) setWorkflowId(workflows.data[0].id);
  }, [workflows.data, workflowId]);

  const create = useMutation({
    mutationFn: () => {
      // The spot goes along when it's the job's address (for the county's records).
      const at = position && (elsewhere || isNew) ? { position } : {};
      const jobFields = { workflowId, title, salesRepId: salesRepId || null, leadSource, ...(elsewhere ? address : {}), ...at };
      if (isNew) return post<JobDetail>("/api/jobs/with-customer", { customer: nc, job: jobFields });
      if (!customer) throw new Error("Pick the customer, or add a new one.");
      return post<JobDetail>("/api/jobs", { customerId: customer.id, ...jobFields });
    },
    onSuccess: async (j) => {
      await qc.invalidateQueries({ queryKey: ["contact", j.customerId] });
      await qc.invalidateQueries({ queryKey: ["jobs"] });
      onClose();
      navigate(`/jobs/${j.id}`);
    },
  });

  return (
    <Dialog
      title={given ? `New job for ${given.displayName}` : "New job"}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="job-form" busy={create.isPending} disabled={!workflowId || (!given && !isNew && !customer)}>
            Add job
          </Button>
        </>
      }
    >
      <form
        id="job-form"
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        {create.error && <Alert>{errorMessage(create.error)}</Alert>}
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="secondary" className="px-3 py-1.5" busy={locating} onClick={useMyLocation}>
            📍 Use my location
          </Button>
          <span className="text-xs text-muted">Fills in the address here, and the owner from the county's records.</span>
        </div>
        {locateError && <Alert>{locateError}</Alert>}
        {property && <PropertyFound p={property} />}
        {!!sameAddress.data?.items.length && (
          <Alert tone="info">
            <div className="font-semibold">Already a job at this address?</div>
            <ul className="mt-1">
              {sameAddress.data.items.map((j) => (
                <li key={j.id}>
                  <a href={`/jobs/${j.id}`} target="_blank" rel="noreferrer" className="font-medium text-brand underline">
                    #{j.number} {j.customerName}
                  </a>{" "}
                  · {[j.stageName, j.street, j.city].filter(Boolean).join(" · ")}
                </li>
              ))}
            </ul>
          </Alert>
        )}
        {!given && (
          <div className="flex flex-col gap-3 rounded-[var(--radius-control)] bg-mist/60 p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-ink-soft">Customer</span>
              <button type="button" className="text-sm font-semibold text-brand hover:underline" onClick={() => (setIsNew(!isNew), setPicked(null))}>
                {isNew ? "Pick an existing customer" : "+ New customer"}
              </button>
            </div>
            {!isNew ? (
              <ContactPicker label="Find the customer" value={picked} onChange={setPicked} />
            ) : (
              <>
                {customerTypes.length > 1 && (
                  <SelectField label="Customer type" value={nc.typeId} onChange={(e) => setNc({ ...nc, typeId: e.target.value })}>
                    {customerTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </SelectField>
                )}
                <div className="inline-flex self-start rounded-[var(--radius-control)] bg-white p-1">
                  {(["residential", "commercial"] as const).map((k) => (
                    <button key={k} type="button" onClick={() => setNc({ ...nc, kind: k })} className={cx("rounded-[6px] px-3 py-1 text-sm font-semibold", nc.kind === k ? "bg-mist" : "text-muted hover:text-ink")}>
                      {k === "residential" ? "Residential" : "Commercial"}
                    </button>
                  ))}
                </div>
                {nc.kind === "commercial" && <Field label="Company name" required value={nc.company} onChange={(e) => setNc({ ...nc, company: e.target.value })} />}
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={nc.kind === "commercial" ? "Contact first name" : "First name"} value={nc.firstName} onChange={(e) => setNc({ ...nc, firstName: e.target.value })} />
                  <Field label={nc.kind === "commercial" ? "Contact last name" : "Last name"} value={nc.lastName} onChange={(e) => setNc({ ...nc, lastName: e.target.value })} />
                  <Field label="Mobile phone" type="tel" value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} />
                  <Field label="Email" type="email" value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} />
                </div>
                <AddressFields value={nc} onChange={(a) => setNc((x) => ({ ...x, ...a }))} legend="Their address" onPlaced={(p) => p.lat !== null && p.lon !== null && void learn({ lat: p.lat, lon: p.lon })} />
                {!!maybe.data?.items.length && (
                  <Alert tone="info">
                    <div className="font-semibold">Already in your contacts?</div>
                    <ul className="mt-1">
                      {maybe.data.items.map((d) => (
                        <li key={d.id}>
                          <button type="button" className="font-medium text-brand underline" onClick={() => (setIsNew(false), setPicked({ id: d.id, name: d.displayName }))}>
                            {d.displayName}
                          </button>{" "}
                          · {[d.typeName, formatPhone(d.phone), d.city].filter(Boolean).join(" · ")}
                        </li>
                      ))}
                    </ul>
                  </Alert>
                )}
              </>
            )}
          </div>
        )}
        <SelectField label="Job type" value={workflowId} onChange={(e) => setWorkflowId(e.target.value)} required>
          {workflows.data?.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </SelectField>
        <Field label="Short description (optional)" placeholder="Roof and gutters" value={title} onChange={(e) => setTitle(e.target.value)} />
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectField label="Commissioned rep" value={salesRepId} onChange={(e) => setRep(e.target.value)}>
            <option value="">Nobody yet</option>
            {users.data
              ?.filter((u) => u.active)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
          </SelectField>
          <SelectField label="Lead source" value={leadSource} onChange={(e) => setLeadSource(e.target.value)}>
            <option value="">Not set</option>
            {me?.lists.leadSources.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </SelectField>
        </div>
        <div>
          <div className="text-sm font-semibold text-ink-soft">Job address</div>
          <label className="mt-1 flex items-center gap-2">
            <input type="radio" checked={!elsewhere} onChange={() => setElsewhere(false)} className="accent-[var(--color-brand)]" />
            <span>{(isNew ? addressLine(nc) : customer ? addressLine(customer) : "") || "Customer's address (none on file)"}</span>
          </label>
          <label className="mt-1 flex items-center gap-2">
            <input type="radio" checked={elsewhere} onChange={() => setElsewhere(true)} className="accent-[var(--color-brand)]" />
            <span>Somewhere else</span>
          </label>
        </div>
        {elsewhere && <AddressFields value={address} onChange={setAddress} onPlaced={(p) => p.lat !== null && p.lon !== null && void learn({ lat: p.lat, lon: p.lon })} />}
      </form>
    </Dialog>
  );
}

/** What the county has at the spot (and Google's picture of it), shown while making the job. */
function PropertyFound({ p }: { p: PropertyPreview }) {
  const [pic, setPic] = useState(true);
  const r = p.record;
  if (!r && !p.streetViewUrl && !p.searchUrl) return null;
  return (
    <div className="flex gap-3 rounded-[var(--radius-control)] bg-mist/60 p-3 text-sm">
      {p.streetViewUrl && pic && <img src={p.streetViewUrl} alt="Google Street View" className="h-20 w-28 shrink-0 rounded-[var(--radius-control)] object-cover" onError={() => setPic(false)} />}
      <div className="min-w-0">
        {r ? (
          <>
            <div className="font-semibold">Owner per {r.county}: {r.owner}</div>
            <div className="text-ink-soft">
              {[r.marketValueCents ? `Market value ${formatCents(r.marketValueCents).replace(/\.00$/, "")}` : null, r.sqft ? `${r.sqft.toLocaleString()} sq ft` : null, r.yearBuilt ? `built ${r.yearBuilt}` : null].filter(Boolean).join(" · ")}
            </div>
            <div className="text-xs text-muted">{r.asOf ? `County data as of ${r.asOf}. ` : ""}A suggestion: check the name with them. It's saved on the job.</div>
          </>
        ) : (
          <div className="text-ink-soft">
            {p.county ? `${p.county}'s records can't be read automatically.` : "No county records found here."}{" "}
            {p.searchUrl && (
              <a href={p.searchUrl} target="_blank" rel="noreferrer" className="font-semibold text-brand hover:underline">
                Look up on the assessor's site
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Dollar input that stores cents. */
export function MoneyField({ label, cents, onChange, hint }: { label: string; cents: number | null; onChange: (c: number | null) => void; hint?: string }) {
  const [text, setText] = useState(cents === null ? "" : formatCents(cents).replace("$", ""));
  const parsed = parseDollars(text);
  return (
    <Field
      label={label}
      inputMode="decimal"
      value={text}
      hint={hint}
      error={Number.isNaN(parsed) ? "Enter an amount like 1,500.00" : undefined}
      onChange={(e) => {
        setText(e.target.value);
        const c = parseDollars(e.target.value);
        if (!Number.isNaN(c)) onChange(c);
      }}
      onBlur={() => {
        if (parsed !== null && !Number.isNaN(parsed)) setText(formatCents(parsed).replace("$", ""));
      }}
    />
  );
}

/** Search and pick a contact (or clear it). */
export function ContactPicker({ label, value, onChange, hint, category }: { label: string; value: { id: string; name: string } | null; onChange: (c: { id: string; name: string } | null) => void; hint?: string; category?: "vendor" | "subcontractor" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const results = useQuery({ queryKey: ["contact-search", q, category], queryFn: () => get<{ items: ContactListItem[] }>(`/api/contacts?q=${encodeURIComponent(q)}&limit=8${category ? `&category=${category}` : ""}`), enabled: open && q.trim().length >= 2 });
  return (
    <div className="relative">
      <span className="mb-1 block text-sm font-semibold text-ink-soft">{label}</span>
      {value && !open ? (
        <div className="flex items-center gap-2 rounded-[var(--radius-control)] border border-line px-3 py-2">
          <span className="flex-1">{value.name}</span>
          <button type="button" className="text-sm text-muted hover:text-ink" onClick={() => setOpen(true)}>
            Change
          </button>
          <button type="button" className="text-sm text-muted hover:text-danger" onClick={() => onChange(null)}>
            Clear
          </button>
        </div>
      ) : (
        <input
          aria-label={label}
          className="w-full rounded-[var(--radius-control)] border border-line px-3 py-2"
          placeholder="Search contacts"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => setQ(e.target.value)}
        />
      )}
      {open && results.data && (
        <ul className="absolute z-10 mt-1 w-full rounded-[var(--radius-control)] border border-line bg-white shadow-lg">
          {results.data.items.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="flex w-full justify-between px-3 py-2 text-left hover:bg-mist"
                onClick={() => {
                  onChange({ id: c.id, name: c.displayName });
                  setOpen(false);
                  setQ("");
                }}
              >
                <span>{c.displayName}</span>
                <span className="text-sm text-muted">{c.typeName}</span>
              </button>
            </li>
          ))}
          {!results.data.items.length && <li className="px-3 py-2 text-sm text-muted">No matches.</li>}
        </ul>
      )}
      {hint && <p className="mt-1 text-sm text-muted">{hint}</p>}
    </div>
  );
}
